import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as externalReviewApi from "@/services/externalReviewApi";
import type { ExternalReviewCountParams } from "@/services/externalReviewApi";
import type {
  ExternalReviewCreate,
  ExternalReviewListParams,
  SentimentUpdate,
} from "@/types/externalReview";
import { sourcesKeys } from "./useSources";

export const externalReviewKeys = {
  all: ["external-reviews"] as const,
  list: (params?: ExternalReviewListParams) =>
    [...externalReviewKeys.all, "list", params ?? {}] as const,
  count: (params?: ExternalReviewCountParams) =>
    [...externalReviewKeys.all, "count", params ?? {}] as const,
  detail: (reviewId: number) =>
    [...externalReviewKeys.all, "detail", reviewId] as const,
};

export function useExternalReviewsList(params?: ExternalReviewListParams) {
  return useQuery({
    queryKey: externalReviewKeys.list(params),
    queryFn: () => externalReviewApi.listExternalReviews(params),
  });
}

export function useExternalReviewsCount(params?: ExternalReviewCountParams) {
  return useQuery({
    queryKey: externalReviewKeys.count(params),
    queryFn: () => externalReviewApi.getExternalReviewsCount(params),
  });
}

export function useExternalReview(reviewId: number | null) {
  return useQuery({
    queryKey: externalReviewKeys.detail(reviewId ?? -1),
    queryFn: () => externalReviewApi.getExternalReview(reviewId as number),
    enabled: reviewId != null,
  });
}

function invalidateExternalReviewQueries(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  queryClient.invalidateQueries({ queryKey: externalReviewKeys.all });
  queryClient.invalidateQueries({ queryKey: sourcesKeys.all });
}

export function useUpdateSentiment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      reviewId,
      ...body
    }: { reviewId: number } & SentimentUpdate) =>
      externalReviewApi.updateSentiment(reviewId, body),
    onSuccess: () => {
      invalidateExternalReviewQueries(queryClient);
    },
  });
}

export function useIngestExternalReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sourceId,
      body,
    }: {
      sourceId: string;
      body: ExternalReviewCreate;
    }) => externalReviewApi.ingestExternalReview(sourceId, body),
    onSuccess: () => {
      invalidateExternalReviewQueries(queryClient);
    },
  });
}

export function useIngestExternalReviewsBulk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sourceId,
      reviews,
    }: {
      sourceId: string;
      reviews: ExternalReviewCreate[];
    }) => externalReviewApi.ingestExternalReviewsBulk(sourceId, reviews),
    onSuccess: () => {
      invalidateExternalReviewQueries(queryClient);
    },
  });
}
